import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import {
  SUGGESTION_ANNOTATION_COMMAND_TYPES,
  SUGGESTION_ANNOTATION_PREDICATE_TYPES,
} from 'modules/common/viewer/pdfViewer/annotationCommands/consts';
import { buildFieldSuggestionAnnotations } from 'modules/common/viewer/pdfViewer/annotationCommands/suggestionUtils';
import {
  SuggestionAnnotationRaw,
  SuggestionAnnotationStateUpdate,
} from 'modules/common/viewer/pdfViewer/annotationCommands/types';
import { suggestionAnnotations } from 'modules/common/viewer/pdfViewer/data/actions';
import { useExtractedTableAnnotations } from 'modules/common/viewer/pdfViewer/hooks/useExtractedTableAnnotations';
import { useCroppedTableInteractions } from 'modules/extractedTables/hooks/useCroppedTableInteractions';

import { useInferenceInfo } from './useInferenceInfo';

type PdfInteractionsState = {
  showAllExtractions: boolean;
};

type PdfInteractionsActions = {
  toggleShowAllExtractions: () => void;
};

type PdfInteractionsContextValue = {
  state: PdfInteractionsState;
  actions: PdfInteractionsActions;
};

const PdfInteractionsContext =
  createContext<PdfInteractionsContextValue | null>(null);
PdfInteractionsContext.displayName = 'PdfInteractionsContext';

type PdfInteractionsProviderProps = {
  children: React.ReactNode;
};

export const PdfInteractionsProvider = ({
  children,
}: PdfInteractionsProviderProps) => {
  const [showAllExtractions, setShowAllExtractionsState] =
    useState<boolean>(true);

  const toggleShowAllExtractions = useCallback(() => {
    setShowAllExtractionsState((previous) => !previous);
  }, []);

  const { suggestionsList, extractedTables } = useInferenceInfo();

  const { extractedTableAnnotations } = useExtractedTableAnnotations({
    extractedTables,
  });

  const suggestionAnnotationsList = useMemo(() => {
    if (suggestionsList.data) {
      return buildFieldSuggestionAnnotations(suggestionsList.data);
    } else {
      return [];
    }
  }, [suggestionsList.data]);

  const allAnnotations = useMemo(() => {
    return [...suggestionAnnotationsList, ...extractedTableAnnotations];
  }, [suggestionAnnotationsList, extractedTableAnnotations]);

  const dispatch = useDispatch();

  // load annotations
  useEffect(() => {
    dispatch(
      suggestionAnnotations.queueCommands({
        commands: [
          {
            type: SUGGESTION_ANNOTATION_COMMAND_TYPES.LOAD,
            annotations: allAnnotations,
          },
        ],
      }),
    );
  }, [dispatch, allAnnotations]);

  useEffect(() => {
    // process all visibility state management
    const isAnnotHidden = () => {
      return !showAllExtractions;
    };

    const getColorCode = (annot: SuggestionAnnotationRaw) => {
      return annot.colorCode;
    };

    // get current state, then get the next state, then queue commands
    const nextState: SuggestionAnnotationStateUpdate = {};
    for (const annot of allAnnotations) {
      // NOTE: do not manipulate focus and hidden here as that happens transiently from
      //  focus commands
      nextState[annot.id] = {
        isHidden: isAnnotHidden(),
        colorCode: getColorCode(annot),
      };
    }
    dispatch(
      suggestionAnnotations.queueCommands({
        commands: [
          // turn all off that are no longer referenced
          {
            type: SUGGESTION_ANNOTATION_COMMAND_TYPES.HIDE,
            predicate: {
              type: SUGGESTION_ANNOTATION_PREDICATE_TYPES.NOT_IN_NEXT_STATE,
              nextStateAnnotationIds: Object.keys(nextState),
            },
          },
          {
            type: SUGGESTION_ANNOTATION_COMMAND_TYPES.MERGE_STATE_MULTIPLE,
            mergeState: nextState,
          },
        ],
      }),
    );
  }, [showAllExtractions, dispatch, allAnnotations]);

  useCroppedTableInteractions();

  const value = useMemo<PdfInteractionsContextValue>(
    () => ({
      state: {
        showAllExtractions,
      },
      actions: {
        toggleShowAllExtractions,
      },
    }),
    [showAllExtractions, toggleShowAllExtractions],
  );

  return (
    <PdfInteractionsContext.Provider value={value}>
      {children}
    </PdfInteractionsContext.Provider>
  );
};

export function usePdfInteractions(): PdfInteractionsContextValue {
  const context = useContext(PdfInteractionsContext);
  if (context == null) {
    throw new Error(
      'usePdfInteractions must be used within a PdfInteractionsProvider',
    );
  }
  return context;
}
